import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth";
import { brand, showFullName } from "../lib/brand";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}
const items: NavItem[] = [
  { to: "/admin", label: "ダッシュボード", icon: "🏠", end: true },
  { to: "/admin/members", label: "会員管理", icon: "👥" },
  { to: "/admin/events", label: "イベント管理", icon: "🗓" },
  { to: "/admin/checkin", label: "受付", icon: "📷" },
  { to: "/admin/contents", label: "コンテンツ", icon: "🎥" },
  { to: "/admin/products", label: "おすすめ商材", icon: "🛍" },
  { to: "/admin/notices", label: "お知らせ", icon: "📢" },
  { to: "/admin/hearings", label: "ヒアリング", icon: "📝" },
  { to: "/admin/hearing-design", label: "ヒアリング設計", icon: "🛠" },
  { to: "/admin/settings/payment", label: "支払い設定", icon: "💳" },
  { to: "/admin/payment-integrations", label: "決済連携設定", icon: "🔗" },
  { to: "/admin/audit-logs", label: "監査ログ", icon: "📋" },
];

export function AdminLayout() {
  const { me } = useAuth();
  const roles = me?.user.roles ?? [];
  // 受付スタッフ (staff のみ) は /api/admin/* を叩けないため、開いても 403 になる
  // メニューは出さない。受付だけを見せる。
  const isAdmin = roles.includes("admin") || roles.includes("super_admin");
  const navItems = isAdmin ? items : items.filter((i) => i.to === "/admin/checkin");

  return (
    <div className="min-h-screen flex flex-col bg-tora-paper">
      <header
        className="bg-tora-ink text-white border-b-2 border-tora-accent"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
          <Link
            to={isAdmin ? "/admin" : "/admin/checkin"}
            className="font-bold text-white text-base md:text-lg tracking-wide flex items-baseline gap-2 [@media(pointer:coarse)]:py-2"
          >
            <span
              className="text-tora-accent font-extrabold tracking-wider"
              style={{ fontFamily: "'Noto Serif JP', 'Times New Roman', serif" }}
            >
              {brand.shortName}
            </span>
            {/* スマホでは横幅が足りず「管理画面」が 1 文字ずつ縦積みになるため、
                サービス名は sm 以上でのみ出す（CFL + 管理画面 は常に表示）。 */}
            {showFullName && <span className="hidden sm:inline">{brand.name}</span>}
            <span className="ml-1 text-xs text-tora-accent font-semibold align-middle whitespace-nowrap">
              管理画面
            </span>
          </Link>
          <Link
            to="/"
            className="tap-target ml-auto text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap"
          >
            ← <span className="hidden sm:inline">会員サイトへ</span>戻る
          </Link>
        </div>
      </header>
      <div className="flex-1 mx-auto w-full max-w-6xl px-4 py-6 md:py-10 grid grid-cols-12 gap-6 md:gap-8">
        <aside className="col-span-12 md:col-span-3">
          <nav className="flex md:flex-col gap-1 [@media(pointer:coarse)]:gap-2 overflow-x-auto md:overflow-visible -mx-4 md:mx-0 px-4 md:px-0">
            {navItems.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                    isActive
                      ? "bg-tora text-white font-semibold shadow-sm"
                      : "text-tora-ink/80 hover:bg-tora/5 hover:text-tora"
                  }`
                }
              >
                <span className="text-base" aria-hidden="true">
                  {i.icon}
                </span>
                <span>{i.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="col-span-12 md:col-span-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
